import { changeDpiDataUrl } from 'changedpi';
import { saveAs } from 'file-saver';
import { ttf2eot } from 'fonteditor-core';
import { jsPDF } from 'jspdf';
import { uniq } from 'lodash';
import pako from 'pako';
import PptxGenJS from '@meitu/js-to-pptx';
import xDesignWatermark from '@/assets/x-design-watermark.png';
import { getBehavior } from '@/framework';
import { Result } from '@/framework/plugins';
import { createCanvas, loadImageAsync, setFileExtension } from '@/utils/file-kit';
import { alibabaFontReplaceToSourceHanSansCN } from '@/utils/font-kit';
import { getDPI, pxToInch } from '@/utils/unit-kit';
import { IExportPlugin, ILayerProps, IResultCanvas, ITextLayerProps, LayerType, PluginType } from '@/version-types';

export class Download extends Result {
    private async getWatermarkCanvas() {
        const img = await loadImageAsync(xDesignWatermark, false);
        return createCanvas(img.width, img.height, img).canvas;
    }

    private woff2eot(buffer: ArrayBuffer, options: Record<string, unknown> = {}) {
        options.inflate = pako.inflate;
        const eotBuffer = ttf2eot(buffer, options);
        return eotBuffer;
    }

    private async ttfUrlToBlob(fontUrl: string) {
        try {
            // Step 1: Fetch the font file from the given URL
            const response = await fetch(fontUrl);
            // Step 2: Ensure the response is OK (status in the range 200-299)
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            // Step 3: Convert the response to a Blob
            const buffer = await response.arrayBuffer();
            const eotBuffer = await this.woff2eot(buffer);
            const unit8Array = new Uint8Array(eotBuffer);
            const blob = new Blob([unit8Array], {
                type: 'font/opentype',
            });
            // Return the Blob URL
            return blob;
        } catch (error) {
            console.error(`Failed to convert font URL to Blob: ${error}`);
            return null;
        }
    }

    async drawPPTCover(slices: IResultCanvas[], fullPageNoUseLastPage = false) {
        const stageWidth = slices[0].width;
        const { canvas, ctx } = createCanvas(stageWidth, slices[0].canvas.height * 2);
        const len = slices.length;

        for (let i = 0; i < len; i++) {
            const { width, height, canvas } = slices[i] || {};
            if (!canvas) continue;
            let index = i;
            if (i >= 4 && !fullPageNoUseLastPage) {
                if (i !== len - 1) continue;
                index = 4;
                // console.log({ index, i, len, canvas });
            }

            // 除第一页外的新增行数
            const lineCount = ~~((index - 1) / 2);

            const dx = index ? (index % 2 ? 0 : width / 2 + 1) : 0;
            const dy = index ? (lineCount / 2 + 1) * height + lineCount + 2 : 0;
            const dWidth = index ? width / 2 : width + 1;
            const dHeight = index ? height / 2 : height + 1;

            ctx.drawImage(
                canvas, // img,
                0,
                0,
                width,
                height,
                dx,
                dy,
                dWidth,
                dHeight,
            );
            const lineWidth = 10;
            // 绘制边框
            ctx.beginPath();
            ctx.lineWidth = lineWidth;
            ctx.strokeStyle = 'rgba(255,255,255,.7)';

            switch (index) {
                case 0:
                    ctx.moveTo(0, dHeight - 1);
                    ctx.lineTo(dWidth, dHeight - 1);
                    break;
                case 1:
                case 3:
                    ctx.moveTo(0, dy + 1);
                    ctx.lineTo(dWidth - 1, dy + 1);
                    ctx.lineTo(dWidth - 1, dy + dHeight - 1);
                    index === 1 && ctx.lineTo(0, dy + dHeight - 1);
                    break;
                case 2:
                case 4:
                    ctx.moveTo(stageWidth, dy + 1);
                    ctx.lineTo(dx + 1, dy + 1);
                    ctx.lineTo(dx + 1, dy + dHeight - 1);
                    index === 2 && ctx.lineTo(stageWidth, dy + dHeight - 1);
                    break;
                default:
                    break;
            }
            ctx.stroke();
        }

        return canvas;
    }

    /**
     * 保存为图片
     */
    async saveAsImage(
        name: string,
        itemName: string | string[],
        images: IResultCanvas[],
        extension: string,
        quality: number,
        dpi = 72,
        autoDownload = true,
        isIntl = true,
        copyRight?: string,
        hideIndexOnFirstItem = false, // 是否隐藏第一个item的序号
    ) {
        this.isAbort = false;
        if (images.length <= 0) return;

        name = name.replace(/\//g, '_');
        // 记录itemName的重复次数
        const itemNameIndexs: number[] = [];
        if (Array.isArray(itemName)) {
            const itemNameRepeatMap = new Map<string, number>();
            itemName = itemName.map((item) => {
                const index = itemNameRepeatMap.get(item) || 0;
                const name = item.replace(/\//g, '_');

                itemNameIndexs.push(index);
                itemNameRepeatMap.set(item, index + 1);
                return name;
            });
        } else {
            itemName = itemName.replace(/\//g, '_');
        }

        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (let i = 0; i < images.length; i++) {
            const { canvas } = images[i];
            let dataUrl = canvas.toDataURL(setFileExtension(extension), quality);
            if (dpi > 72) {
                dataUrl = changeDpiDataUrl(dataUrl, dpi);
            }
            dataUrl = dataUrl.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
            if (Array.isArray(itemName)) {
                // 只有重复的图片才需要添加序号
                const hasCopyRight = copyRight !== undefined;
                const fileName = `${itemName[i]}${hasCopyRight ? copyRight : isIntl ? '_Zawa' : '_美图设计室'}${itemNameIndexs[i] === 0 && hideIndexOnFirstItem ? '' : `（${itemNameIndexs[i] + 1}）`}.${extension}`;
                zip.file(fileName, dataUrl, {
                    base64: true,
                });
            } else {
                zip.file(`${itemName}${i === 0 && hideIndexOnFirstItem ? '' : `（${i + 1}）`}.${extension}`, dataUrl, {
                    base64: true,
                });
            }
        }
        const blob = await zip.generateAsync({
            type: 'blob',
        });
        if (autoDownload) {
            saveAs(blob, `${name}.zip`);
        }
        return blob;
    }

    /**
     * 保存为 PPT 可编辑
     */
    async saveAsPPT(name: string, pages: number[] = [], showWaterMark = false, autoDownload = true) {
        this.isAbort = false;
        const pagesSet: Set<number> = new Set(pages);
        const pptx = new PptxGenJS();
        const stages = this.framework.getStages();
        const { width, height } = stages[0]?.[0] || { width: 1, height: 1 };
        pptx.defineLayout({
            name,
            width: pxToInch(width),
            height: pxToInch(height),
        });
        pptx.layout = name;

        let watermark: HTMLCanvasElement | undefined;
        if (showWaterMark) {
            watermark = await this.getWatermarkCanvas();
        }
        let fontFamilyArr: string[] = [];
        for (let i = 0; i < stages.length; i++) {
            if (pagesSet.size && !pagesSet.has(i)) continue;
            const layers = stages[i];
            const allLayers: ILayerProps[] = [];
            const traverse = (layer: ILayerProps) => {
                (layer.children || []).forEach((item: ILayerProps) => {
                    traverse(item);
                });

                if (layer.width !== 0 && layer.height !== 0) {
                    allLayers.push(layer);
                }
            };
            for (let i = 0; i < layers.length; i++) {
                const layer = layers[i];
                traverse(layer);
            }
            const slide = pptx.addSlide();
            for (let i = 0; i < allLayers.length; i++) {
                const layer = allLayers[i];
                const savePPTLayerAsync = getBehavior(this.identity, layer)?.savePPTLayerAsync;
                if (savePPTLayerAsync) {
                    if (layer.layerType === LayerType.TEXT_LAYER) {
                        (layer.data as ITextLayerProps).fontFamily = alibabaFontReplaceToSourceHanSansCN(
                            (layer.data as ITextLayerProps).fontFamily,
                        );
                    }
                    await savePPTLayerAsync(slide, layer, width, height, watermark, fontFamilyArr);
                }
            }
            // 添加水印图层
            if (watermark) {
                const options: PptxGenJS.ImageProps = {
                    x: pxToInch(width - watermark.width - 50),
                    y: pxToInch(height - watermark.height - 50),
                    w: pxToInch(watermark.width),
                    h: pxToInch(watermark.height),
                    rotate: 0,
                };

                options.data = watermark?.toDataURL() || '';
                slide.addImage(options);
            }
        }
        fontFamilyArr = uniq(fontFamilyArr);

        for (let i = 0; i < fontFamilyArr.length; i++) {
            const fontName = fontFamilyArr[i];
            const font = this.framework.getFont(fontName);
            if (font && font.zip_url) {
                const blob = await this.ttfUrlToBlob(font.zip_url);
                if (blob) {
                    pptx.addFont({
                        typeface: fontName,
                        fontBlob: blob,
                    });
                }
            }
        }

        if (autoDownload) {
            pptx.writeFile({ fileName: `${name}.pptx` });
        }

        return await pptx.write({
            outputType: 'blob',
        });
    }

    /**
     * 保存为 PPT 不可编辑
     */
    async saveAsPPTNotEdit(name: string, images: IResultCanvas[], showWaterMark = false, autoDownload = true) {
        this.isAbort = false;
        if (images.length <= 0) return;

        const pptx = new PptxGenJS();
        const { width, height } = images[0];
        pptx.defineLayout({
            name,
            width: pxToInch(width),
            height: pxToInch(height),
        });
        pptx.layout = name;
        for (let j = 0; j < images.length; j++) {
            const slide = pptx.addSlide();
            const options = {
                x: 0,
                y: 0,
                w: pxToInch(width),
                h: pxToInch(height),
            } as PptxGenJS.ImageProps;
            if (showWaterMark) {
                const watermarkCanvas = await this.getWatermarkCanvas();
                images[j].canvas
                    .getContext('2d')
                    ?.drawImage(
                        watermarkCanvas,
                        0,
                        0,
                        watermarkCanvas.width,
                        watermarkCanvas.height,
                        width - watermarkCanvas.width - 50,
                        height - watermarkCanvas.height - 50,
                        watermarkCanvas.width,
                        watermarkCanvas.height,
                    );
            }
            options.data = images[j].canvas.toDataURL();
            slide.addImage(options);
        }
        if (autoDownload) {
            pptx.writeFile({ fileName: `${name}.pptx` });
        }
        // 转blob
        return await pptx.write({
            outputType: 'blob',
        });
    }

    /**
     * 保存为 PDF
     */
    async saveAsPDF(name: string, images: IResultCanvas[], showWaterMark = false, autoDownload = true) {
        this.isAbort = false;

        if (images.length <= 0) return;

        const doc = new jsPDF({
            unit: 'in',
        });

        for (let i = 0; i < images.length; i++) {
            const { width, height, canvas } = images[i];

            if (showWaterMark) {
                const watermarkCanvas = await this.getWatermarkCanvas();
                canvas
                    .getContext('2d')
                    ?.drawImage(
                        watermarkCanvas,
                        0,
                        0,
                        watermarkCanvas.width,
                        watermarkCanvas.height,
                        width - watermarkCanvas.width - 50,
                        height - watermarkCanvas.height - 50,
                        watermarkCanvas.width,
                        watermarkCanvas.height,
                    );
            }

            // 为解决页面背景可能有透明度，而 JPEG 不能像 PNG 那样支持透明度会出现黑色背景，这边统一加一层白色背景
            const whiteBackgroundCanvas = document.createElement('canvas');
            const context = whiteBackgroundCanvas.getContext('2d');

            whiteBackgroundCanvas.width = width;
            whiteBackgroundCanvas.height = height;

            if (context) {
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, width, height);
                context.drawImage(canvas, 0, 0, width, height);
            }

            const dpi = getDPI() || 72;

            // 使用 pxToInch 转换像素到英寸，传入实际 DPI
            const widthInInches = pxToInch(width, dpi);
            const heightInInches = pxToInch(height, dpi);

            doc.addPage([widthInInches, heightInInches], widthInInches - heightInInches > 0 ? 'l' : 'p');

            // 通过使用 JPEG 格式来使 PDF 文件大小最小化，之前使用 PNG 格式，PDF 文件会很大
            doc.addImage(whiteBackgroundCanvas, 'JPEG', 0, 0, widthInInches, heightInInches, undefined, 'FAST');
        }

        doc.deletePage(1);
        if (autoDownload) {
            doc.save(`${name}.pdf`);
        }

        return doc.output('blob');
    }

    /**
     * 拍平数组 才能调用drawLayers 不然drawLayers不会遍历子图层
     * @param layers - 图层数据
     */
    getDrawLayers(layers: ILayerProps[]) {
        const resultLayers = [] as ILayerProps[];

        const getChildrens = (layer: ILayerProps) => {
            const childs = [] as ILayerProps[];
            for (let j = 0; j < layer.children.length; j++) {
                const child = layer.children[j];
                if (child.layerType === LayerType.GROUP_LAYER) {
                    childs.push(...getChildrens(child));
                } else {
                    childs.push(child);
                }
            }

            return childs;
        };

        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            if (layer.layerType === LayerType.GROUP_LAYER) {
                resultLayers.push(...getChildrens(layer));
            } else {
                resultLayers.push(layer);
            }
        }

        return resultLayers;
    }
}

/**
 * 下载插件扩展
 */
export const downloadExtension: IExportPlugin = {
    sort: 0,
    type: PluginType.EXTENSION,
    name: 'download-extension',
    createInstance({ identity }) {
        return [{ key: 'Download', value: new Download(identity) }];
    },
};
